import type { Listing } from "@housing/shared";

import type { IndexedDistrict } from "@/districts/district.model.ts";
import { ageInDays } from "@/listings/listing.format.ts";
import { distanceMeters, pointInMultiPolygon, type LngLat } from "@/map/geo.ts";
import { PRICE_ANY, SQFT_NONE, TRAVEL_ANY, type AppState } from "@/search/search.state.ts";
import { travelMinutesFor } from "@/travel/travel.model.ts";

/**
 * The bed-count buckets the filter exposes. `3` means "3 bedrooms or more" —
 * the tail past three is thin enough that splitting it further would just make
 * four chips where three do the job.
 */
export const BED_BUCKETS = [0, 1, 2, 3] as const;
export type BedBucket = (typeof BED_BUCKETS)[number];

export const BED_LABELS: Record<BedBucket, string> = {
  0: "Studio",
  1: "1 BR",
  2: "2 BR",
  3: "3+ BR",
};

/** Map a raw bed count onto its filter bucket; null beds have no bucket. */
export function bedBucket(beds: number | null): BedBucket | null {
  if (beds == null) return null;
  if (beds < 0.5) return 0;
  if (beds < 1.5) return 1;
  if (beds < 2.5) return 2;
  return 3;
}

/** Per-facet counts, so the panel can say what each control is actually costing. */
export interface FilterResult {
  visible: Listing[];
  total: number;
  /** How many survive everything except the age limit. */
  droppedByAge: number;
  droppedByBeds: number;
  droppedByArea: number;
  droppedByPrice: number;
  droppedBySqft: number;
  /**
   * Of `droppedBySqft`, how many were dropped for having no size at all rather
   * than for being too small — only ever non-zero with the "include unknown"
   * checkbox off. Separated for the same reason as the travel split below.
   */
  droppedBySqftUnknown: number;
  droppedByTravel: number;
  /**
   * Of `droppedByTravel`, how many were dropped for having no time at all by the
   * chosen mode rather than for being too far. Worth separating: "40 too far" is
   * the filter working, "40 not priced" is a coverage gap the reader should know
   * about before trusting the result.
   */
  droppedByTravelUnknown: number;
}

/**
 * Apply the whole filter stack. Each "dropped by X" count is measured with X
 * removed and the other facets applied, so the numbers read as "widening this
 * one control would add N" rather than as overlapping subtotals.
 */
export function applyFilters(
  listings: Listing[],
  state: AppState,
  districtIndex: Map<string, IndexedDistrict>,
  now = Date.now(),
): FilterResult {
  const byAge = (l: Listing) => ageInDays(l.postedAt, now) <= state.maxAgeDays;

  const beds = new Set(state.beds);
  const byBeds = (l: Listing) => {
    const bucket = bedBucket(l.beds);
    return bucket === null ? state.includeUnknownBeds : beds.has(bucket);
  };

  // Every listing that reaches the client has a price — the API drops the ones
  // that don't — so this is a plain comparison. Both ends live in one predicate
  // and one dropped-by count: too cheap and too expensive are the same answer
  // ("outside budget"), just from different directions.
  const byPrice = (l: Listing) =>
    l.price >= state.minPrice && (state.maxPrice >= PRICE_ANY || l.price <= state.maxPrice);

  // A *floor*, unlike price and travel: the question is "is it at least this
  // big". With no floor set everything passes, unmeasured units included, so
  // the checkbox only ever matters while a minimum is active.
  const sqftCapped = state.minSqft > SQFT_NONE;
  const bySqft = (l: Listing) => {
    if (!sqftCapped) return true;
    if (l.sqft == null) return state.includeUnknownSqft;
    return l.sqft >= state.minSqft;
  };

  const byArea = areaPredicate(state, districtIndex);

  // A listing with no time for the chosen mode fails an active limit: we can't
  // promise a trip we never priced. It's counted separately so the panel can say
  // so rather than letting it read as "too far".
  const travelCapped = state.maxTravelMin < TRAVEL_ANY;
  const untimed = (l: Listing) => travelMinutesFor(l.travel, state.travelMode) == null;
  const byTravel = (l: Listing) => {
    if (!travelCapped) return true;
    const mins = travelMinutesFor(l.travel, state.travelMode);
    return mins != null && mins <= state.maxTravelMin;
  };

  const visible: Listing[] = [];
  let droppedByAge = 0;
  let droppedByBeds = 0;
  let droppedByArea = 0;
  let droppedByPrice = 0;
  let droppedBySqft = 0;
  let droppedBySqftUnknown = 0;
  let droppedByTravel = 0;
  let droppedByTravelUnknown = 0;

  for (const l of listings) {
    const a = byAge(l);
    const b = byBeds(l);
    const c = byArea(l);
    const d = byPrice(l);
    const e = byTravel(l);
    const f = bySqft(l);
    if (a && b && c && d && e && f) visible.push(l);
    else if (!a && b && c && d && e && f) droppedByAge++;
    else if (a && !b && c && d && e && f) droppedByBeds++;
    else if (a && b && !c && d && e && f) droppedByArea++;
    else if (a && b && c && !d && e && f) droppedByPrice++;
    else if (a && b && c && d && !e && f) {
      droppedByTravel++;
      if (untimed(l)) droppedByTravelUnknown++;
    } else if (a && b && c && d && e && !f) {
      droppedBySqft++;
      if (l.sqft == null) droppedBySqftUnknown++;
    }
  }

  return {
    visible,
    total: listings.length,
    droppedByAge,
    droppedByBeds,
    droppedByArea,
    droppedByPrice,
    droppedBySqft,
    droppedBySqftUnknown,
    droppedByTravel,
    droppedByTravelUnknown,
  };
}

function areaPredicate(
  state: AppState,
  districtIndex: Map<string, IndexedDistrict>,
): (l: Listing) => boolean {
  const { area } = state;
  if (area.mode === "all") return () => true;

  if (area.mode === "radius") {
    const center: LngLat = area.center;
    return (l) => distanceMeters(center, [l.lon, l.lat]) <= area.radiusM;
  }

  const selected = area.ids
    .map((id) => districtIndex.get(id))
    .filter((d): d is IndexedDistrict => d !== undefined);
  // Polygons not yet loaded (or an id from an older bake) shouldn't silently
  // blank the map — fall through to showing everything until they resolve.
  if (selected.length === 0) return () => true;

  return (l) => {
    for (const d of selected) {
      if (l.lon < d.bbox[0] || l.lon > d.bbox[2] || l.lat < d.bbox[1] || l.lat > d.bbox[3]) {
        continue;
      }
      if (pointInMultiPolygon(l.lon, l.lat, d.geometry)) return true;
    }
    return false;
  };
}

/**
 * Listings as a MapLibre-ready FeatureCollection. Only the fields the map layers
 * actually style with are promoted to properties; the popup reads the full
 * listing out of a lookup keyed by id, so cluster tiles stay small.
 */
export interface ListingFeatureProps {
  id: string;
  ageDays: number;
  price: number;
  priceLabel: string;
  seen: boolean;
  tracked: boolean;
}

export function toFeatureCollection(
  listings: Listing[],
  marks: { seen?: ReadonlySet<string>; tracked?: ReadonlySet<string> } = {},
  now = Date.now(),
): GeoJSON.FeatureCollection<GeoJSON.Point, ListingFeatureProps> {
  return {
    type: "FeatureCollection",
    features: listings.map((l) => {
      const h = hashId(l.id);
      return {
        type: "Feature" as const,
        id: h,
        geometry: { type: "Point" as const, coordinates: displayedLngLat(l) },
        properties: {
          id: l.id,
          ageDays: ageInDays(l.postedAt, now),
          price: l.price,
          // Precomputed so the pill label costs no expression work per frame.
          priceLabel: compactPrice(l.price),
          // Scalars only in here. Feature properties travel through vt-pbf on
          // their way out of the GeoJSON worker, and an array-valued property
          // corrupts the tile's feature index — the map still *draws*, but
          // every queryRenderedFeatures (i.e. every click) then throws.
          /** Already opened. Drives the pin's *form*, never its colour. */
          seen: marks.seen?.has(l.id) ?? false,
          /** On the shortlist. The one mark that does take the colour over. */
          tracked: marks.tracked?.has(l.id) ?? false,
        },
      };
    }),
  };
}

/** ±12 m at SF's latitude, in degrees. */
const JITTER_M = 12;
const LAT_DEG_PER_M = 1 / 111_320;
const LON_DEG_PER_M = 1 / (111_320 * Math.cos((37.77 * Math.PI) / 180));

/**
 * A deterministic scatter of at most JITTER_M metres, derived from the id hash
 * so a listing never wanders between refreshes.
 *
 * Listings in one building share coordinates exactly, so their marks stack to
 * a single pixel-perfect pile in which only the top one can ever be clicked.
 * Scattering the *geometry* (rather than an icon offset) keeps the pill, its
 * dot, and its invisible hit-target on the same point, and it scales the right
 * way: a few pixels at neighbourhood zoom, a visibly fanned-out stack by the
 * time someone is zoomed in close enough to pick pills apart — while never
 * moving a mark further than the width of the building it stands on.
 */
function jitter(lon: number, lat: number, h: number): [number, number] {
  const dx = (((h >>> 8) & 0xff) / 255 - 0.5) * 2 * JITTER_M;
  const dy = (((h >>> 16) & 0xff) / 255 - 0.5) * 2 * JITTER_M;
  return [lon + dx * LON_DEG_PER_M, lat + dy * LAT_DEG_PER_M];
}

/**
 * Where a listing is actually drawn — its coordinates plus the scatter above.
 * Exported so the map's own screen-space hit-testing measures against the
 * point the pill really occupies, not the point the source reported.
 */
export function displayedLngLat(l: Listing): [number, number] {
  return jitter(l.lon, l.lat, hashId(l.id));
}

export function compactPrice(price: number): string {
  if (price < 1000) return `$${price}`;
  const k = price / 1000;
  return `$${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * MapLibre feature ids must be numeric for feature-state, but listing ids are
 * strings like "zumper:64952084". FNV-1a keeps that mapping stable across
 * renders so hover state survives a data refresh.
 */
export function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
